import { ViewByOptions } from "@/components/common/SummaryCards";
import { useAppTheme } from "@/hooks/useAppTheme";
import { XYChartDataKeyType, ViewByClaimType } from "@/types";
import { CurrencyFormatterNotationOption, formatUSD } from "@/utils/formatters/currencyFormatter";
import { darken, lighten } from "@mui/material/styles";

/**
 * Theme-aware chart color palette.
 *
 * Uses the theme's semantic colors (primary, secondary, info, success, warning, error)
 * as anchor points, ensuring each color is visually distinct from its neighbors.
 * Dark mode uses lighter/brighter variants for readability on dark backgrounds.
 */
export const useChartPalette = () => {
  const { theme, isDarkMode } = useAppTheme();
  const p = theme.palette;

  // Each slot uses a different semantic color to maximize hue separation.
  // Neighbors should never share the same base hue.
  if (isDarkMode) {
    return [
      p.primary.light, // teal/orange (depends on theme)
      lighten(p.warning.main, 0.2), // amber
      p.info.light, // blue
      lighten(p.success.main, 0.3), // green
      p.secondary.light, // navy/gold
      lighten(p.error.main, 0.25), // red/pink
      lighten(p.primary.main, 0.45), // lighter primary
      lighten(p.info.main, 0.4), // lighter blue
      lighten(p.warning.main, 0.45), // lighter amber
      lighten(p.success.main, 0.5), // lighter green
    ];
  }

  return [
    p.primary.main, // teal
    p.warning.dark ?? darken(p.warning.main, 0.2), // amber
    p.info.main, // blue
    darken(p.success.main, 0.15), // green
    p.secondary.main, // navy
    p.error.main, // red
    p.primary.dark, // darker primary
    darken(p.info.main, 0.25), // darker blue
    darken(p.warning.main, 0.1), // darker amber
    darken(p.success.main, 0.3), // darker green
  ];
};

export const CHART_COLORS = [
  "#8884d8",
  "#82ca9d",
  "#ffc658",
  "#ff8042",
  "#a4de6c",
  "#d0ed57",
  "#8dd1e1",
  "#d88884",
  "#bc82ca",
  "#84ffd8",
];

export const getInitialXYDataKeys = (data: XYChartDataKeyType[]) => {
  const availableKeys = Object.keys(data[0]);
  const initialXAxisDataKey = availableKeys[0];
  const initialYAxisDataKey = availableKeys[1];

  const initialDataKeys = {
    xAxisDataKey: initialXAxisDataKey,
    yAxisDataKey: initialYAxisDataKey,
  };

  return initialDataKeys;
};

export const getFormattedValue = (value: number, viewBy?: ViewByOptions) => {
  switch (viewBy) {
    case ViewByClaimType.CLAIM_AMOUNT:
      return formatUSD(value, {
        notation: CurrencyFormatterNotationOption.COMPACT,
        minimumFractionDigits: 0,
      });
    case ViewByClaimType.CLAIM_COUNT:
    default:
      return value.toLocaleString();
  }
};

export const getShowTicks = (data: XYChartDataKeyType[]) => data.length < 5;

export const getLabelPlaceholder = (showTicks: boolean) => (!showTicks ? "Hover to View Labels" : "");
